import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import BookingCalendar from '@/components/feature/BookingCalendar';
import type { BookingCalendarTexts } from '@/components/feature/BookingCalendar';

// ─── Booking calendar texts (pt-BR, implantation context) ───────────────────

const bookingTexts: BookingCalendarTexts = {
  stepDateTitle: 'Escolha a data',
  stepDateSubtitle: 'Selecione um dia disponível para sua implantação',
  stepTimeTitle: 'Escolha o horário',
  stepTimeSubtitle: 'Selecione o melhor horário para sua sessão',
  stepFormTitle: 'Seus dados',
  monthNames: [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ],
  dayAbbreviations: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
  todayLabel: 'Hoje',
  prevMonth: 'Mês anterior',
  nextMonth: 'Próximo mês',
  noSlotsDate: 'Nenhum horário disponível nesta data.',
  noSlotsTime: 'Nenhum horário disponível para esta data.',
  selectTimePrompt: 'Horários disponíveis:',
  nameLabel: 'Nome completo',
  namePlaceholder: 'Seu nome completo',
  phoneLabel: 'WhatsApp / Telefone',
  phonePlaceholder: '(XX) XXXXX-XXXX',
  notesLabel: 'Observações (opcional)',
  notesPlaceholder: 'Detalhes sobre o que você precisa configurar...',
  submitButton: 'Confirmar agendamento',
  submittingText: 'Confirmando...',
  backToDate: 'Voltar para data',
  backToTime: 'Voltar para horário',
  modifyTime: 'Alterar horário',
  selectedDateLabel: 'Data',
  selectedTimeLabel: 'Horário',
  successTitle: 'Agendamento confirmado!',
  successMessage: 'Sua sessão de implantação foi agendada com sucesso. Nossa equipe entrará em contato pelo WhatsApp informado.',
  loadingSlots: 'Carregando horários disponíveis...',
  errorFetch: 'Não foi possível carregar os horários. Tente novamente.',
  errorSubmit: 'Erro ao confirmar agendamento. Tente novamente.',
  retryButton: 'Tentar novamente',
  doneButton: 'Concluído',
  dateNextButton: 'Continuar para horário',
};

interface SetupPackage {
  id: string;
  name: string;
  price: string;
  priceRaw: number;
  tag: string;
  description: string;
  features: string[];
  icon: string;
  highlighted: boolean;
  timeline: string;
}

const setupPackages: SetupPackage[] = [
  {
    id: 'setup-basic',
    name: 'Implantação Básica',
    price: 'R$ 297',
    priceRaw: 297,
    tag: 'Essencial',
    description: 'Ideal para negócios que estão começando e precisam de um WhatsApp configurado rapidamente.',
    icon: 'ri-rocket-line',
    highlighted: false,
    timeline: '1-2 dias úteis',
    features: [
      'Configuração de 1 WhatsApp (Meta ou Instância Própria)',
      'Integração com API oficial',
      'Treinamento básico de 1h via videoconferência',
      'Configuração de webhook para tracking',
      'Suporte por email por 7 dias',
    ],
  },
  {
    id: 'setup-complete',
    name: 'Implantação Completa',
    price: 'R$ 997',
    priceRaw: 997,
    tag: 'Recomendada',
    description: 'Para empresas que precisam de múltiplos WhatsApps e integração completa com CRM e automações.',
    icon: 'ri-star-line',
    highlighted: true,
    timeline: '3-5 dias úteis',
    features: [
      'Configuração de até 5 WhatsApps (Meta + Instância Própria)',
      'Integração com API + CRM',
      'Treinamento completo de 4h via videoconferência',
      'Configuração de webhooks avançados',
      'Migração de dados de outras plataformas',
      'Setup de automações e segmentações',
      'Suporte prioritário por 30 dias',
      'Sessão de revisão após 15 dias',
    ],
  },
];

const faqs = [
  {
    question: 'Preciso já ter um plano WZap PRO para contratar a implantação?',
    answer: 'Sim! Recomendamos que você já tenha um plano ativo (Starter, Professional, Business ou Enterprise) para que possamos configurar tudo dentro do seu painel. Mas se preferir, podemos agendar a implantação e você assina o plano durante a sessão.',
  },
  {
    question: 'Quanto tempo leva para o WhatsApp ficar pronto para uso?',
    answer: 'Na implantação básica, em até 2 dias úteis seu WhatsApp estará conectado e pronto para enviar campanhas. Na implantação completa, o prazo é de 3 a 5 dias úteis, pois inclui migração de dados e configuração de automações.',
  },
  {
    question: 'E se eu já tenho o WhatsApp Business conectado em outro lugar?',
    answer: 'Sem problemas! Nossa equipe faz a migração completa — desconectamos do sistema anterior e conectamos ao WZap PRO sem perder seu número nem seus contatos. A migração está inclusa na implantação completa.',
  },
  {
    question: 'Como funciona o treinamento?',
    answer: 'O treinamento é feito ao vivo por videoconferência (Google Meet). Na versão básica, é 1h focada em operação de campanhas. Na completa, são 4h cobrindo desde importação de contatos até automações avançadas e leitura de relatórios.',
  },
  {
    question: 'Posso contratar apenas a implantação sem o plano mensal?',
    answer: 'A implantação é um serviço de configuração. Para continuar usando o sistema, você precisa de um plano mensal ativo. A boa notícia é que depois de configurado, o uso é simples e autônomo.',
  },
];

export default function SetupFeePage() {
  const { user } = useAuth();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [bookingSaved, setBookingSaved] = useState(false);

  const handleAppointmentConfirmed = async (data: {
    customer_name: string;
    customer_phone: string;
    start_time: string;
    end_time: string;
    notes?: string;
  }) => {
    if (!user) return;
    const { error } = await supabase.from('appointments').insert({
      user_id: user.id,
      customer_name: data.customer_name,
      customer_phone: data.customer_phone,
      start_time: data.start_time,
      end_time: data.end_time,
      notes: data.notes || null,
      status: 'confirmed',
    });
    if (!error) setBookingSaved(true);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
          Implantação e Setup
        </h1>
        <p className="text-sm text-foreground-500 dark:text-foreground-400">
          Nossa equipe configura tudo para você. Escolha o pacote ideal e agende sua implantação.
        </p>
      </div>

      {/* Info Alert */}
      <div className="bg-primary-50 dark:bg-primary-500/5 border border-primary-200 dark:border-primary-500/15 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-primary-100 dark:bg-primary-500/10 flex items-center justify-center flex-shrink-0">
          <i className="ri-lightbulb-flash-line text-xl text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground-950 dark:text-foreground-100 mb-1">
            Não quer configurar sozinho? A gente faz pra você!
          </h3>
          <p className="text-xs text-foreground-600 dark:text-foreground-400 leading-relaxed">
            A implantação cobre desde a conexão do WhatsApp até o treinamento da sua equipe.
            Você escolhe o pacote, agenda um horário e nosso time cuida de tudo.
            Em até 48h seu WhatsApp está 100% operacional no WZap PRO.
          </p>
        </div>
      </div>

      {/* Packages */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {setupPackages.map((pkg) => (
          <div
            key={pkg.id}
            className={`relative bg-white dark:bg-background-100 rounded-2xl border p-6 md:p-7 flex flex-col ${
              pkg.highlighted
                ? 'border-primary-500 ring-4 ring-primary-500/10'
                : 'border-background-200 dark:border-background-300'
            }`}
          >
            {pkg.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                {pkg.tag}
              </span>
            )}

            {/* Icon */}
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${
              pkg.highlighted
                ? 'bg-primary-50 dark:bg-primary-500/10'
                : 'bg-accent-50 dark:bg-accent-500/10'
            }`}>
              <i className={`${pkg.icon} text-2xl ${pkg.highlighted ? 'text-primary-600 dark:text-primary-400' : 'text-accent-600 dark:text-accent-400'}`} />
            </div>

            <h3 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
              {pkg.name}
            </h3>
            <p className="text-sm text-foreground-500 dark:text-foreground-400 mb-4 leading-relaxed">
              {pkg.description}
            </p>

            {/* Price */}
            <div className="mb-5 pb-5 border-b border-background-100 dark:border-background-200">
              <p className="text-3xl font-heading font-extrabold text-foreground-950 dark:text-foreground-100">
                {pkg.price}
              </p>
              <p className="text-xs text-foreground-500 mt-1">pagamento único</p>
              <div className="flex items-center gap-1.5 mt-3">
                <i className="ri-time-line text-foreground-400 text-sm" />
                <span className="text-xs text-foreground-500">{pkg.timeline}</span>
              </div>
            </div>

            {/* Features */}
            <ul className="space-y-3 flex-1 mb-6">
              {pkg.features.map((feat, j) => (
                <li key={j} className="flex items-start gap-3 text-sm text-foreground-700 dark:text-foreground-300">
                  <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              type="button"
              className={`w-full text-sm font-semibold px-5 py-3 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
                pkg.highlighted
                  ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-sm hover:shadow-md active:scale-[0.98]'
                  : 'bg-background-50 dark:bg-background-200 text-foreground-700 dark:text-foreground-300 hover:bg-background-100 dark:hover:bg-background-300 border border-background-200 dark:border-background-300'
              }`}
            >
              <Link to="/subscription" className="flex items-center gap-2 w-full justify-center">
                <i className="ri-shopping-cart-line" />
                Contratar {pkg.name}
              </Link>
            </button>
          </div>
        ))}
      </div>

      {/* Booking Calendar */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-accent-50 dark:bg-accent-500/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-calendar-check-line text-2xl text-accent-600 dark:text-accent-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-2">
              Agende sua implantação
            </h3>
            <p className="text-sm text-foreground-500 dark:text-foreground-400 leading-relaxed">
              Escolha o melhor dia e horário, preencha seus dados e nossa equipe cuida de tudo por videoconferência.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <i className="ri-check-line" />
              Videoconferência ao vivo
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <i className="ri-check-line" />
              Compartilhamento de tela
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:text-emerald-400">
              <i className="ri-check-line" />
              Gravação disponível
            </span>
          </div>
        </div>

        {bookingSaved && (
          <div className="mb-5 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-500/8 border border-emerald-200 dark:border-emerald-500/15 flex items-center gap-3">
            <i className="ri-check-double-line text-emerald-600 dark:text-emerald-400 text-lg" />
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              Agendamento registrado com sucesso! Você também pode acompanhar no Dashboard.
            </p>
          </div>
        )}

        <BookingCalendar
          timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/af769db2-1da7-4c35-b18c-5d64e19667fa.eb6de40a1c4b7e9328524b17068fc5a5ee24d59c70bd953a57b4c0a222e4cad2"
          appointmentsApi="https://readdy.ai/api/public/calendar/appointments/af769db2-1da7-4c35-b18c-5d64e19667fa.eb6de40a1c4b7e9328524b17068fc5a5ee24d59c70bd953a57b4c0a222e4cad2"
          texts={bookingTexts}
          onAppointmentConfirmed={handleAppointmentConfirmed}
        />
      </div>

      {/* How it works */}
      <div>
        <h2 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-6 text-center">
          Como funciona a implantação
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {[
            { step: '01', title: 'Contrate o pacote', desc: 'Escolha Básica ou Completa e faça o pagamento único pelo MercadoPago.', icon: 'ri-shopping-cart-line' },
            { step: '02', title: 'Agende a sessão', desc: 'Escolha o melhor dia e horário para a videoconferência com nosso especialista.', icon: 'ri-calendar-line' },
            { step: '03', title: 'Configuração ao vivo', desc: 'Em chamada de vídeo, conectamos seus WhatsApps e configuramos tudo em tempo real.', icon: 'ri-vidicon-line' },
            { step: '04', title: 'Treinamento', desc: 'Sua equipe aprende na prática a operar campanhas, contatos e relatórios no painel.', icon: 'ri-user-star-line' },
          ].map((item) => (
            <div key={item.step} className="text-center">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/15 flex items-center justify-center mb-4">
                <i className={`${item.icon} text-xl text-primary-600 dark:text-primary-400`} />
              </div>
              <span className="text-[11px] font-bold text-primary-500 dark:text-primary-400 uppercase tracking-widest block mb-2">
                {item.step}
              </span>
              <h4 className="text-sm font-heading font-semibold text-foreground-900 dark:text-foreground-100 mb-1.5">
                {item.title}
              </h4>
              <p className="text-xs text-foreground-500 dark:text-foreground-400 leading-relaxed max-w-[200px] mx-auto">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 md:p-8">
        <h2 className="text-xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-6">
          Dúvidas frequentes
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border border-background-200 dark:border-background-300 rounded-xl overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-50 dark:hover:bg-background-200 transition-colors"
              >
                <span className="text-sm font-medium text-foreground-900 dark:text-foreground-100 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`text-foreground-400 text-lg flex-shrink-0 transition-transform duration-200 ${
                    expandedFaq === i ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                  }`}
                />
              </button>
              {expandedFaq === i && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="text-center py-6">
        <p className="text-sm text-foreground-500 dark:text-foreground-400 mb-3">
          Ainda tem dúvidas? Fale diretamente com nossa equipe.
        </p>
        <a
          href="mailto:implantacao@wzappro.com.br"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 transition-colors"
        >
          <i className="ri-mail-line" />
          implantacao@wzappro.com.br
        </a>
      </div>
    </div>
  );
}